<template>
  <div class="dropdown-items-page py-3">
    <header class="page-toolbar">
      <div class="page-toolbar__title">
        <span class="page-toolbar__accent" aria-hidden="true"></span>
        <div>
          <div class="page-toolbar__eyebrow">Settings</div>
          <h1>Dropdown Items</h1>
          <p>Manage option dictionaries with fast add and remove actions</p>
        </div>
      </div>
      <div class="page-toolbar__actions">
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          title="Refresh"
          aria-label="Refresh items"
          @click="loadItems"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="page-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="page-alert">{{ successMessage }}</CAlert>

    <section class="section-card add-card">
      <header class="section-card__header">
        <h2 class="section-card__title">Add New Item</h2>
        <span class="section-card__hint">Name, slug and type are required</span>
      </header>
      <div class="section-card__body">
        <div class="add-grid">
          <label class="field">
            <span class="field__label">Name</span>
            <input
              v-model.trim="form.name"
              type="text"
              class="field__input"
              placeholder="e.g. Not Interested, Number Disconnected"
              :disabled="isSaving"
              @keyup.enter="createItem"
            >
          </label>
          <label class="field">
            <span class="field__label">Slug</span>
            <input
              v-model.trim="form.slug"
              type="text"
              class="field__input"
              placeholder="e.g. not_interested"
              :disabled="isSaving"
              @keyup.enter="createItem"
            >
          </label>
          <label class="field">
            <span class="field__label">
              Type
              <button
                v-if="existingTypes.length"
                type="button"
                class="field__toggle"
                :disabled="isSaving"
                @click="toggleTypeMode"
              >{{ useNewType ? 'Pick existing' : '+ New type' }}</button>
            </span>
            <AppDropdown
              v-if="!useNewType && existingTypes.length"
              :value="form.type"
              :options="existingTypes"
              placeholder="Select a type"
              searchable
              search-placeholder="Search types…"
              :disabled="isSaving"
              @input="form.type = $event"
            />
            <input
              v-else
              v-model.trim="form.type"
              type="text"
              class="field__input"
              placeholder="e.g. region"
              :disabled="isSaving"
              @keyup.enter="createItem"
            >
          </label>
          <label class="field">
            <span class="field__label">Note <span class="field__optional">(optional)</span></span>
            <input
              v-model.trim="form.note"
              type="text"
              class="field__input"
              placeholder="Internal note"
              :disabled="isSaving"
              @keyup.enter="createItem"
            >
          </label>
        </div>
        <div class="add-actions">
          <button
            type="button"
            class="submit-btn"
            :disabled="isSaving"
            @click="createItem"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>{{ isSaving ? 'Adding…' : 'Add New Item' }}</span>
          </button>
        </div>
      </div>
    </section>

    <section class="section-card list-card">
      <header class="section-card__header section-card__header--list">
        <div class="section-card__heading">
          <h2 class="section-card__title">All Items</h2>
          <span class="section-card__count" v-if="items.length">{{ items.length }} total</span>
        </div>
        <div class="search-bar">
          <span class="search-bar__icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>
          <input
            v-model.trim="searchTerm"
            type="text"
            class="search-bar__input"
            placeholder="Search by name, slug, type or note…"
            aria-label="Search items"
          >
          <button
            v-if="searchTerm"
            type="button"
            class="search-bar__clear"
            aria-label="Clear search"
            @click="searchTerm = ''"
          >×</button>
        </div>
      </header>

      <BaseTable
        :items="filteredItems"
        :columns="tableColumns"
        :loading="isLoading"
        :empty-text="searchTerm ? 'No items match the current search.' : 'No items yet.'"
        :show-count="false"
        :bordered="false"
        paginated
        :page-size="20"
      >
        <template #cell-actions="{ row }">
          <button
            type="button"
            class="row-delete-btn"
            :disabled="isDeletingId === row.id"
            @click="deleteItem(row)"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
            <span>{{ isDeletingId === row.id ? 'Removing…' : 'Delete' }}</span>
          </button>
        </template>
      </BaseTable>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'

export default {
  name: 'DropdownItems',
  components: { AppDropdown },
  data () {
    return {
      items: [],
      searchTerm: '',
      form: {
        name: '',
        slug: '',
        type: '',
        note: ''
      },
      useNewType: false,
      isLoading: false,
      isSaving: false,
      isDeletingId: null,
      errorMessage: '',
      successMessage: '',
      tableColumns: [
        { key: 'id', label: 'ID', align: 'right', width: '70px' },
        { key: 'name', label: 'Name' },
        { key: 'slug', label: 'Slug' },
        { key: 'type', label: 'Type' },
        { key: 'note', label: 'Note' },
        { key: 'actions', label: '', sortable: false, align: 'right', width: '110px' }
      ]
    }
  },
  computed: {
    filteredItems () {
      const q = this.searchTerm.toLowerCase()
      if (!q) return this.items

      return this.items.filter((item) => {
        return [item.id, item.name, item.slug, item.type, item.note]
          .filter((value) => value !== null && value !== undefined && value !== '')
          .some((value) => String(value).toLowerCase().includes(q))
      })
    },
    existingTypes () {
      const seen = new Set()
      const types = []
      this.items.forEach((item) => {
        const type = item && item.type ? String(item.type).trim() : ''
        if (type && !seen.has(type)) {
          seen.add(type)
          types.push({ value: type, label: type })
        }
      })
      return types.sort((a, b) => a.label.localeCompare(b.label))
    }
  },
  created () {
    this.loadItems()
  },
  methods: {
    token () {
      return localStorage.getItem('api_token')
    },
    toggleTypeMode () {
      this.useNewType = !this.useNewType
      this.form.type = ''
    },
    parseError (error, fallback) {
      const validationErrors = error?.response?.data?.error
      if (validationErrors && typeof validationErrors === 'object') {
        const key = Object.keys(validationErrors)[0]
        if (key && Array.isArray(validationErrors[key]) && validationErrors[key][0]) {
          return validationErrors[key][0]
        }
      }
      return error?.response?.data?.message || fallback
    },
    loadItems () {
      this.errorMessage = ''
      this.successMessage = ''
      this.isLoading = true

      axios.get(`${this.$apiAdress}/api/drop_down_items?token=${this.token()}`)
        .then((response) => {
          this.items = Array.isArray(response.data) ? response.data : []
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to load dropdown items.')
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    createItem () {
      this.errorMessage = ''
      this.successMessage = ''

      if (!this.form.name || !this.form.slug || !this.form.type) {
        this.errorMessage = 'Name, slug and type are required.'
        return
      }

      this.isSaving = true
      axios.post(
        `${this.$apiAdress}/api/drop_down_items?token=${this.token()}`,
        {
          name: this.form.name,
          slug: this.form.slug,
          type: this.form.type,
          note: this.form.note
        }
      )
        .then(() => {
          this.successMessage = 'Dropdown item added successfully.'
          this.form = { name: '', slug: '', type: '', note: '' }
          this.useNewType = false
          this.loadItems()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to add dropdown item.')
        })
        .finally(() => {
          this.isSaving = false
        })
    },
    deleteItem (item) {
      this.errorMessage = ''
      this.successMessage = ''
      this.isDeletingId = item.id

      axios.delete(`${this.$apiAdress}/api/drop_down_items/${item.id}?token=${this.token()}`)
        .then((response) => {
          this.successMessage = response?.data?.message || 'Item removed successfully.'
          this.loadItems()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Unable to delete dropdown item.')
        })
        .finally(() => {
          this.isDeletingId = null
        })
    }
  }
}
</script>

<style scoped>
.dropdown-items-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.page-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.page-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.page-toolbar__accent {
  width: 4px;
  height: 44px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.page-toolbar__eyebrow {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #66748a;
  margin-bottom: 0.1rem;
}

.page-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.page-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.page-toolbar__actions {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

/* Refresh button — outlined white, neutral, with rotating icon */
.page-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.page-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.page-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.page-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.page-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: refresh-spin 0.9s linear infinite;
  color: #16a07a;
}

@keyframes refresh-spin {
  to { transform: rotate(360deg); }
}

/* ── Alerts ── */
.page-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Section card ── */
.section-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.section-card + .section-card {
  margin-top: 0.85rem;
}

.add-card {
  overflow: visible;
}

.add-card .section-card__header {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.section-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f6;
  flex-wrap: wrap;
}

.section-card__heading {
  display: inline-flex;
  align-items: baseline;
  gap: 0.6rem;
}

.section-card__title {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.section-card__hint {
  font-size: 0.74rem;
  color: #94a3b8;
}

.section-card__count {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #475569;
  background: #eef2f7;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
}

.section-card__body {
  padding: 1rem;
}

/* ── Add new item ── */
.add-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.85rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin: 0;
}

.field__label {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4a5872;
  letter-spacing: 0.01em;
}

.field__optional {
  color: #94a3b8;
  font-weight: 500;
}

.field__toggle {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.72rem;
  font-weight: 600;
  color: #0e5b46;
  cursor: pointer;
  letter-spacing: 0.01em;
}

.field__toggle:hover:not(:disabled) {
  color: #16a07a;
  text-decoration: underline;
}

.field__toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.field >>> .app-dropdown__trigger {
  height: 38px;
}

.field__input {
  height: 38px;
  padding: 0 0.7rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.86rem;
  color: #1f2a44;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.field__input::placeholder {
  color: #9aa3b2;
}

.field__input:focus {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.field__input:disabled {
  background: #f8fafc;
  color: #94a3b8;
  cursor: not-allowed;
}

.add-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 38px;
  padding: 0 1.1rem;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.submit-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.35), 0 6px 14px rgba(14, 91, 70, 0.28);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}

/* ── Search bar (header of list card) ── */
.search-bar {
  position: relative;
  flex: 1 1 280px;
  min-width: 220px;
  max-width: 360px;
  display: inline-flex;
  align-items: center;
}

.search-bar__icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  display: inline-flex;
  pointer-events: none;
}

.search-bar__input {
  width: 100%;
  height: 36px;
  padding: 0 2.2rem 0 2.1rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.83rem;
  color: #1f2a44;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.search-bar__input:focus {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.search-bar__input::placeholder {
  color: #9aa3b2;
}

.search-bar__clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 999px;
  background: #eef2f7;
  color: #475569;
  font-size: 0.95rem;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.search-bar__clear:hover {
  background: #e2e8f0;
  color: #1f2a44;
}

/* ── Row delete button ── */
.row-delete-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.7rem;
  background: #ffffff;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.row-delete-btn:hover:not(:disabled) {
  background: #dc2626;
  border-color: #dc2626;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(220, 38, 38, 0.25);
}

.row-delete-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.25);
}

.row-delete-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 767px) {
  .page-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .page-toolbar__actions {
    width: 100%;
    justify-content: flex-start;
  }

  .section-card__header {
    flex-direction: column;
    align-items: stretch;
  }

  .search-bar {
    max-width: 100%;
  }

  .add-actions {
    justify-content: stretch;
  }

  .submit-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 575px) {
  .dropdown-items-page {
    padding: 0 0 0.5rem;
  }
  .page-toolbar {
    padding: 0.25rem 0 0.75rem;
    gap: 0.5rem;
  }
  .page-toolbar__accent {
    height: 30px;
  }
  .page-toolbar__title h1 {
    font-size: 1rem;
  }
  .page-toolbar__title p {
    font-size: 0.72rem;
  }
  .page-toolbar__actions >>> .btn {
    height: 32px;
    font-size: 0.76rem;
    padding: 0 0.7rem;
  }
  .section-card {
    border-radius: 10px;
  }
  .section-card__body {
    padding: 0.75rem;
  }
  .add-grid {
    grid-template-columns: 1fr;
  }
}
</style>
